import { Drawer, InputAdornment, Switch, TextField } from '@material-ui/core';
import { cloneDeep, debounce, isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IPublisher } from '~/src/dash/api/console-api';
import {
  createPlatformConnectionOfferHistory,
  fetchPlatformConnectionOfferHistory,
  platformConnectBuyerFinalConfirmation,
  updatePlatformConnection,
} from '~/src/dash/apiV2/platform-connect/platform-connect';
import { SelectPill } from '~/src/dash/components/dropdowns/SelectPill';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IconText } from '~/src/dash/components/IconText';
import { IListViewTableDataCell } from '~/src/dash/components/tables/ListViewTable/common';
import { TextWithBackground } from '~/src/dash/components/TextWithBackground';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Div,
  fontSizing,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPlatformConnectionActionType,
  EPlatformConnectOfferType,
  EPlatformConnectStatus,
  IDemandSourcesIDAndName,
  IPlatformConnectionDealExtRow,
  IPlatformConnectionOfferHistory,
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
  OfferDeclinedCanceledExpired,
  platformConnectOfferTypeToString,
  renderExchangeAdornment,
  RowLineBreak,
} from '~/src/dash/views/platform-connect/common';

interface ICartDrawerProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  publisher: IPublisher;
  offerType?: EPlatformConnectOfferType;
  activeRowInfo: Record<string, IListViewTableDataCell>;
  platformConnection?: IPlatformConnectionWithNamesAndType;
  dealID?: number;
  impressionsGoal?: number;
  demandSources?: IDemandSourcesIDAndName[];
  dealExtensionOpportunitiesRows?: IPlatformConnectionDealExtRow[];
  setDealExtensionOpportunitiesRows?: React.Dispatch<
    React.SetStateAction<IPlatformConnectionDealExtRow[]>
  >;
  onFetchUniqueDemandOpportunitiesList?: (params: IPlatformConnectUiParams) => void;
  fetchPendingOffers: (params: IPlatformConnectUiParams) => void;
  isBuyerFinalAccept: boolean;
  setIsBuyerFinalAccept: React.Dispatch<React.SetStateAction<boolean>>;
  biddersList: Record<string, IListItem>;
  loadState: LoadingState;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<any>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setLoadState: React.Dispatch<React.SetStateAction<LoadingState>>;
  dealName: string;
  handleGetCurrencyValue: (row: Record<string, IListViewTableDataCell>) => string;
}

export const PendingOffersDrawer = ({
  isModalOpen,
  setIsModalOpen,
  publisher,
  activeRowInfo,
  fetchPendingOffers,
  isBuyerFinalAccept,
  setIsBuyerFinalAccept,
  biddersList,
  loadState,
  error,
  setError,
  setSnackbarMessage,
  setLoadState,
  dealName,
  handleGetCurrencyValue,
}: ICartDrawerProps) => {
  const { user, currencyMultiplier, currencySymbol, isSuperUser } = useBaseStore();
  const [currActiveRowInfo, setCurrActiveRowInfo] = useState(activeRowInfo);
  const [isMakingCounterOffer, setIsMakingCounterOffer] = useState(false);
  const [counterOfferValue, setCounterOfferValue] = useState('0');
  const [onlyCancelButton, setOnlyCancelButton] = useState(false);
  const [finalConfirmCounterOffer, setFinalConfirmCounterOffer] = useState(false);

  // notes
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [currNote, setCurrNote] = useState('');
  const [offerHistory, setOfferHistory] = useState([]);

  // buyer final accept states
  const [useBidAdjSwitch, setUseBidAdjSwitch] = useState(false);
  const [targetExistingBiddersSwitch, setTargetExistingBiddersSwitch] = useState(false);
  const [bidAdjPercentage, setBidAdjPercentage] = useState('');
  const [bidAdjValue, setBidAdjValue] = useState('');
  const [currBiddersList, setCurrBiddersList] = useState<Record<string, IListItem>>({});
  const [selectedBidders, setSelectedBidders] = useState<Record<string, IListItem>>({});
  const [sendTxtToSeller, setSendTxtToSeller] = useState(false);
  const [txtToSeller, setTxtToSeller] = useState('');
  const [linkDestination, setLinkDestination] = useState({
    pathname: `/app/pub/${publisher.ID}/change-logs`,
    state: {
      resourceID: '',
    },
  });
  const [isActionPending, setIsActionPending] = useState(false);

  useEffect(() => {
    setCurrBiddersList(biddersList);
  }, [biddersList]);

  useEffect(() => {
    const isBuyer = publisher.ID === activeRowInfo?.BuyerPublisherID?.value;
    const isSeller = publisher.ID === activeRowInfo?.SellerPublisherID?.value;

    const awaitingBuyerResponse =
      activeRowInfo?.ActionPublisherID?.value === activeRowInfo?.SellerPublisherID?.value;
    const awaitingSellerResponse =
      activeRowInfo?.ActionPublisherID?.value === activeRowInfo?.BuyerPublisherID?.value;

    const renderOnlyCancelButton =
      (isBuyer && awaitingSellerResponse) || (isSeller && awaitingBuyerResponse);

    setOnlyCancelButton(renderOnlyCancelButton);
    setCurrActiveRowInfo(activeRowInfo);
    setLinkDestination({
      pathname: `/app/pub/${publisher.ID}/change-logs`,
      state: {
        resourceID: activeRowInfo?.ID?.value,
      },
    });
  }, [activeRowInfo]);

  useEffect(() => {
    setOfferHistory((currActiveRowInfo?.PlatformConnectionOfferHistory?.value || []).reverse());
  }, [currActiveRowInfo]);

  const onCloseDrawer = () => {
    unstable_batchedUpdates(() => {
      setIsModalOpen(false);
      setIsMakingCounterOffer(false);
      setCounterOfferValue('0');
      setIsBuyerFinalAccept(false);
      setIsAddingNote(false);
      setCurrNote('');
      setUseBidAdjSwitch(false);
      setTargetExistingBiddersSwitch(false);
      setBidAdjPercentage('');
      setBidAdjValue('');
      setCurrBiddersList(biddersList);
      setSelectedBidders({});
      setFinalConfirmCounterOffer(false);
      setIsActionPending(false);
    });
  };

  const onChangeCounterOfferValue = (value: string) => {
    setCounterOfferValue(value);
  };

  const onClickCounterOffer = () => {
    if (counterOfferValue === '0') {
      setError(true);
      setSnackbarMessage('Counter Offer CPM must be a valid number.');
    } else {
      track('PC - Counters offer', { is_super_user: isSuperUser }, user.user);
      const updates = {
        ID: parseInt(activeRowInfo?.ID?.value as string, 10),
        ActionPublisherID: parseInt(publisher.ID, 10),
        BidFloor: parseInt(counterOfferValue, 10),
        Status: EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER,
      };
      updatePlatformConnection(
        publisher.ID,
        updates,
        EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_COUNTER_OFFER,
      )
        .then(() => {
          fetchPendingOffers && fetchPendingOffers({ currPage: 0, rowsPerPage: 25 });
          onCloseDrawer();
        })
        .catch(() => {
          setError(true);
          setSnackbarMessage('Error sending counter offer.');
        });
    }
  };

  const onClickCancel = () => {
    setIsMakingCounterOffer(false);
    setCounterOfferValue('0');
    setFinalConfirmCounterOffer(false);
    setError(false);
  };

  const handleRenderStatus = () => {
    const isBuyer = publisher.ID === activeRowInfo?.BuyerPublisherID?.value;
    const isSeller = publisher.ID === activeRowInfo?.SellerPublisherID?.value;

    switch (activeRowInfo?.CustomLeftColumn?.value) {
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_SENT:
        if (isBuyer) {
          return <TextWithBackground color='darkGreen' text='Offer Sent' />;
        } else if (isSeller) {
          return <TextWithBackground color='darkGreen' text='New Offer' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER:
        const awaitingBuyerResponse =
          activeRowInfo?.ActionPublisherID?.value === activeRowInfo?.SellerPublisherID?.value;
        const awaitingSellerResponse =
          activeRowInfo?.ActionPublisherID?.value === activeRowInfo?.BuyerPublisherID?.value;

        if (isBuyer && awaitingBuyerResponse) {
          return <TextWithBackground color='purple' text='New Counter Offer' />;
        } else if (isBuyer && awaitingSellerResponse) {
          return <TextWithBackground color='purple' text='Counter Offer Sent' />;
        } else if (isSeller && awaitingBuyerResponse) {
          return <TextWithBackground color='purple' text='Counter Offer Sent' />;
        } else if (isSeller && awaitingSellerResponse) {
          return <TextWithBackground color='purple' text='New Counter Offer' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT:
        if (isBuyer) {
          return <TextWithBackground color='green' text='Ready to Accept' />;
        } else if (isSeller) {
          return <TextWithBackground color='green' text='Accepted' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED:
        return <TextWithBackground color='red' text='Offer Declined' />;
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED:
        return <TextWithBackground color='maroon' text='Offer Canceled' />;
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED:
        return <TextWithBackground color='peach' text='Offer Expired' />;
      default:
        return <TextWithBackground color='' text='' />;
    }
  };

  const handleAcceptPlatformConnection = () => {
    track('PC - Accepts offer', { is_super_user: isSuperUser }, user.user);
    const isBuyer = publisher.ID === activeRowInfo.BuyerPublisherID.value;
    const isSeller = publisher.ID === activeRowInfo.SellerPublisherID.value;

    if (isSeller) {
      const updates = {
        ID: parseInt(activeRowInfo.ID.value, 10),
        ActionPublisherID: parseInt(publisher.ID, 10),
        Status: EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT,
      };
      updatePlatformConnection(
        publisher.ID,
        updates,
        EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_ACCEPT,
      )
        .then(() => {
          fetchPendingOffers({ currPage: 0, rowsPerPage: 25 });
          onCloseDrawer();
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error updating platform connection.');
            setLoadState(LoadingStateNoData);
          });
        });
    }
    if (isBuyer) {
      setIsBuyerFinalAccept(true);
    }
  };

  const handleBuyerFinalConfirmation = () => {
    track(
      'PC - Confirm & Set Live',
      {
        is_super_user: isSuperUser,
        bid_adjustment: !isEmpty(bidAdjValue) || !isEmpty(bidAdjPercentage),
        ads_txt: !isEmpty(txtToSeller),
      },
      user.user,
    );
    setIsActionPending(true);
    const targetedBiddersArr = [];
    for (const bidderID of Object.keys(selectedBidders)) {
      targetedBiddersArr.push(bidderID);
    }

    // pass in current publisher's id in params to create channel on current publisher (buyer)
    // pass in seller's publisherID to create bidder on seller side
    platformConnectBuyerFinalConfirmation(
      parseInt(publisher.ID, 10),
      parseInt(activeRowInfo?.SellerPublisherID?.value, 10),
      activeRowInfo.ID.value,
      targetedBiddersArr,
      activeRowInfo?.DealID?.value ? activeRowInfo.DealID.value : 0,
      activeRowInfo?.Type?.value ===
        platformConnectOfferTypeToString(
          EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION,
        ),
      activeRowInfo.ProposedCPM.value,
      bidAdjPercentage,
      bidAdjValue,
      txtToSeller,
    )
      .then(() => {
        fetchPendingOffers({ currPage: 0, rowsPerPage: 25 });
        const message = 'Successfully established connection with seller.';
        unstable_batchedUpdates(() => {
          setSnackbarMessage(message);
          setError(false);
        });
        onCloseDrawer();
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setError(true);
          setSnackbarMessage('Error updating platform connection.');
        });
      });
  };

  const handleDeclinePlatformConnection = () => {
    track('PC - Declines offer', { is_super_user: isSuperUser }, user.user);
    const updates = {
      ID: parseInt(activeRowInfo?.ID?.value, 10),
      Status: EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED,
    };
    updatePlatformConnection(
      publisher.ID,
      updates,
      EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_DECLINE,
    )
      .then(() => {
        fetchPendingOffers({ currPage: 0, rowsPerPage: 25 });
        onCloseDrawer();
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error updating platform connection.');
        });
      });
  };

  // for when buyer/seller cancels their offer
  const handleCancelPlatformConnection = () => {
    track('PC - Cancels offer', { is_super_user: isSuperUser }, user.user);
    const updates = {
      ID: parseInt(activeRowInfo?.ID?.value, 10),
      Status: EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED,
    };
    updatePlatformConnection(
      publisher.ID,
      updates,
      EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_CANCEL,
    )
      .then(() => {
        fetchPendingOffers({ currPage: 0, rowsPerPage: 25 });
        onCloseDrawer();
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error updating platform connection.');
        });
      });
  };

  const handleSendNote = () => {
    track('PC - Notes', { is_super_user: isSuperUser }, user.user);
    const data = {
      PublisherID: parseInt(publisher.ID, 10),
      PlatformConnectionID: parseInt(activeRowInfo?.ID?.value as string, 10),
      ProposedBidFloor: activeRowInfo?.ProposedCPM?.value,
      ActionType: EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_NOTE,
      UserID: parseInt(user.user.ID, 10),
      Note: currNote,
    } as Partial<IPlatformConnectionOfferHistory>;
    createPlatformConnectionOfferHistory(
      publisher.ID,
      data,
      activeRowInfo?.BuyerPublisherID?.value,
      activeRowInfo?.SellerPublisherID?.value,
    )
      .then(() => {
        fetchPlatformConnectionOfferHistory(publisher.ID, activeRowInfo?.ID?.value)
          .then((history) => {
            const newActiveRowInfo = cloneDeep(currActiveRowInfo);
            if (newActiveRowInfo.PlatformConnectionOfferHistory) {
              newActiveRowInfo.PlatformConnectionOfferHistory.value = history;
              setCurrActiveRowInfo(newActiveRowInfo);
            }
            unstable_batchedUpdates(() => {
              setIsAddingNote(false);
              setCurrNote('');
            });
          })
          .catch(() => {
            unstable_batchedUpdates(() => {
              setError(true);
              setSnackbarMessage('Error sending platform connection note.');
            });
          });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error sending platform connection note.');
        });
      });
  };

  const handleAccept = (
    _key: string,
    alloptions: Record<string, IListItem>,
    selected: Record<string, IListItem>,
  ) => {
    unstable_batchedUpdates(() => {
      setCurrBiddersList(alloptions);
      setSelectedBidders(selected);
    });
  };

  return (
    <>
      <Drawer anchor='right' open={isModalOpen} onClose={onCloseDrawer}>
        {!isBuyerFinalAccept ? (
          <Rows
            gap={spacing.medium}
            style={{ width: '40vw', overflow: 'auto', maxHeight: '100vh' }}
          >
            <Row noVerticalPadding>
              <Rows gap={spacing.medium}>
                <Row noHorizontalPadding paddingBottom={spacing.none}>
                  <Cols
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Col>{handleRenderStatus()}</Col>
                    {/* Link to Change Logs */}
                    <Col>
                      <Cols>
                        <Link
                          to={linkDestination}
                          style={{ textDecoration: 'none', fontSize: fontSizing.small }}
                        >
                          Change Logs
                        </Link>
                      </Cols>
                    </Col>
                    <Col>
                      <TextButton
                        color={publicaColors.darkGray}
                        style={{
                          display: 'flex',
                          borderRadius: borderRadius.small,
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          minWidth: '80px',
                          minHeight: '32px',
                        }}
                        aria-label='Close'
                        onClick={onCloseDrawer}
                      >
                        <IconText
                          bold
                          color={publicaColors.darkGray}
                          icon='close'
                          text='Close'
                          extraSmall
                          gap={spacing.extraSmall}
                        />
                      </TextButton>
                    </Col>
                  </Cols>
                </Row>
                <Row noVerticalPadding paddingLeft={spacing.small}>
                  <Text large heavy color={publicaColors.darkGray}>
                    {!isEmpty(activeRowInfo) ? activeRowInfo.Name.value : 'Anonymous Seller'}
                  </Text>
                </Row>
              </Rows>
            </Row>
            <RowLineBreak />
            <Row noVerticalPadding>
              <Rows gap={spacing.medium}>
                <Row noPadding>
                  {activeRowInfo?.Type?.value === 'Deal Extension' && (
                    <Rows>
                      <Row noPadding>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          icon='handshake'
                          iconColor={publicaColors.black}
                          text='Deal Extension'
                        />
                      </Row>
                      {!isEmpty(dealName) && (
                        <Row noVerticalPadding paddingLeft={spacing.large}>
                          <Text
                            small
                            color={publicaColors.gray}
                            style={{
                              wordWrap: 'break-word',
                            }}
                          >
                            {dealName}
                          </Text>
                        </Row>
                      )}
                    </Rows>
                  )}
                  {activeRowInfo?.Type?.value === 'Unique Demand' &&
                    activeRowInfo?.DemandSource?.value && (
                      <Rows>
                        <Row noPadding>
                          <IconText
                            small
                            bold
                            color={publicaColors.darkGray}
                            icon='globe'
                            text='Demand Source'
                          />
                        </Row>
                        <Row noVerticalPadding paddingLeft={spacing.large}>
                          <Text small color={publicaColors.gray}>
                            {activeRowInfo?.DemandSource?.value}
                          </Text>
                        </Row>
                      </Rows>
                    )}
                </Row>
                <Row noPadding>
                  <Cols
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Cols>
                      <Col>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          icon='shopping'
                          iconColor={publicaColors.black}
                          text='Proposed CPM'
                        />
                      </Col>
                      <Col>
                        <InfoTooltipIcon
                          tooltipText='Offer CPM will be used as Bid Floor'
                          tooltipPlacement='top'
                        />
                      </Col>
                    </Cols>
                    {/* // Existing Pending Offer */}
                    <Col>
                      <Text bold small color={publicaColors.darkGray}>
                        {handleGetCurrencyValue(activeRowInfo)}
                      </Text>
                    </Col>
                  </Cols>
                </Row>
                {isMakingCounterOffer && (
                  <Row noPadding paddingLeft={spacing.large}>
                    <Cols
                      paddingRight={spacing.small}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#6F60DD1A',
                        borderRadius: borderRadius.small,
                      }}
                    >
                      <Col>
                        <Rows
                          style={{
                            padding: '4px 0px 4px 8px',
                          }}
                        >
                          <Row noPadding>
                            <Text heavy small color={'#6F60DD'}>
                              Counter Offer CPM
                            </Text>
                          </Row>
                          <Row noPadding>
                            <Text mini color={publicaColors.darkGray}>
                              Adjust CPM to reflect what you believe is best suitable for this
                              inventory.
                            </Text>
                          </Row>
                        </Rows>
                      </Col>
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <NumberFormat
                          autoFocus
                          name='Counter Offer CPM'
                          value={counterOfferValue === '0' ? '' : counterOfferValue}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeCounterOfferValue(event.target.value)
                          }
                          error={error}
                          customInput={TextField}
                          thousandSeparator={true}
                          InputProps={{
                            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                            endAdornment: renderExchangeAdornment(
                              String(counterOfferValue || 0),
                              currencySymbol,
                              currencyMultiplier,
                            ),
                          }}
                          style={{ maxWidth: '152px' }}
                        />
                      </Col>
                    </Cols>
                  </Row>
                )}
                <Row noPadding>
                  <Rows>
                    <Row noPadding>
                      <Cols
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Col>
                          <IconText
                            small
                            bold
                            gap='6px'
                            color={publicaColors.darkGray}
                            icon='timer'
                            iconColor={publicaColors.black}
                            text='Acceptance window'
                            style={{
                              padding: '4px 6px',
                            }}
                          />
                        </Col>
                        <Col>
                          <Text small bold color={publicaColors.black}>
                            4 weeks
                          </Text>
                        </Col>
                      </Cols>
                    </Row>
                    <Row noVerticalPadding paddingLeft={spacing.large}>
                      <Text small color={publicaColors.darkGray}>
                        {`Time-limit for the offer ${
                          activeRowInfo?.CustomLeftColumn?.value ===
                          EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED
                            ? 'expired'
                            : 'expires'
                        } ${moment(activeRowInfo?.CreatedAt?.value)
                          .add(4, 'week')
                          .fromNow()}`}
                      </Text>
                    </Row>
                  </Rows>
                </Row>
              </Rows>
            </Row>
            <RowLineBreak />
            <Row noVerticalPadding>
              <Rows gap={spacing.small}>
                {!OfferDeclinedCanceledExpired.includes(activeRowInfo?.CustomLeftColumn?.value) ? (
                  <Row noPadding>
                    <Rows>
                      <Row noPadding paddingBottom={spacing.small}>
                        <Cols
                          style={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Col>
                            <Cols>
                              <Col>
                                <IconText
                                  small
                                  bold
                                  color={publicaColors.darkGray}
                                  icon='message'
                                  iconColor={publicaColors.veryDarkGray}
                                  text='Notes'
                                />
                              </Col>
                              <Col>
                                <InfoTooltipIcon
                                  tooltipText='Sending only personal notes will not notify the Publisher. A notification is only sent with accept, decline, or counteroffer at this time.'
                                  tooltipPlacement='top'
                                />
                              </Col>
                            </Cols>
                          </Col>
                          <Col>
                            <ButtonV2
                              style={{
                                borderRadius: '20px',
                              }}
                              clearBackground
                              hoverColor={publicaColors.lighterGray}
                              onClick={() => setIsAddingNote(true)}
                              disabled={
                                !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                  .isAuthorized
                              }
                            >
                              <Text
                                extraSmall
                                style={{
                                  color: publicaColors.blue,
                                }}
                              >
                                + Add personal note
                              </Text>
                            </ButtonV2>
                          </Col>
                        </Cols>
                      </Row>
                      {isAddingNote && (
                        <Row noPadding>
                          <Rows
                            style={{
                              overflow: 'auto',
                              maxHeight: '40vh',
                            }}
                          >
                            <Row
                              noVerticalPadding
                              paddingLeft={spacing.large}
                              paddingRight={spacing.medium}
                            >
                              <FocusedTextfield
                                fullWidth
                                multiline
                                minRows={3}
                                label=''
                                name={'Notes'}
                                value={currNote}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setCurrNote(e.target.value)
                                }
                                disabled={
                                  !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                    .isAuthorized
                                }
                              />
                            </Row>
                            <Row>
                              <Cols
                                gap={spacing.small}
                                style={{
                                  justifyContent: 'flex-end',
                                }}
                              >
                                <Col>
                                  <ButtonV2
                                    style={{
                                      borderRadius: '20px',
                                      minWidth: '80px',
                                      minHeight: '32px',
                                    }}
                                    aria-label='Send'
                                    onClick={handleSendNote}
                                  >
                                    <IconText
                                      bold
                                      extraSmall
                                      color={publicaColors.white}
                                      icon='send'
                                      text='Send'
                                      noPadding
                                    />
                                  </ButtonV2>
                                </Col>
                                <Col>
                                  <ButtonV2
                                    style={{
                                      borderRadius: '20px',
                                      padding: `${spacing.extraSmall} ${spacing.small}`,
                                      minWidth: '80px',
                                      minHeight: '32px',
                                    }}
                                    backgroundColor={publicaColors.lighterGray}
                                    hoverColor={publicaColors.lightGray}
                                    onClick={() => {
                                      setIsAddingNote(false);
                                      setCurrNote('');
                                    }}
                                  >
                                    <Text
                                      bold
                                      extraSmall
                                      style={{
                                        color: publicaColors.darkGray,
                                      }}
                                    >
                                      Cancel
                                    </Text>
                                  </ButtonV2>
                                </Col>
                              </Cols>
                            </Row>
                          </Rows>
                        </Row>
                      )}
                    </Rows>
                  </Row>
                ) : (
                  <Row noPadding>
                    <Cols>
                      <Col>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          icon='message'
                          iconColor={publicaColors.veryDarkGray}
                          text='Notes'
                        />
                      </Col>
                    </Cols>
                  </Row>
                )}
                <Row noPadding>
                  <Rows
                    gap={spacing.large}
                    style={{
                      overflow: 'auto',
                      maxHeight: '30vh',
                    }}
                    extraLargeHorizontalPadding
                    paddingBottom={isEmpty(offerHistory) ? spacing.none : spacing.medium}
                  >
                    {offerHistory.map((history: IPlatformConnectionOfferHistory) => {
                      return (
                        <Row noPadding>
                          <Rows gap={spacing.small}>
                            <Row noPadding>
                              <Cols>
                                <Col paddingRight={spacing.extraSmall}>
                                  {history.ActionType ===
                                  EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_NOTE ? (
                                    <Text extraSmall>
                                      {`${history.User?.firstname} ${history.User?.lastname} -`}
                                    </Text>
                                  ) : (
                                    <Text extraSmall>Automatic Note -</Text>
                                  )}
                                </Col>
                                <Col>
                                  <Text extraSmall>{`[${moment(history.CreatedAt)
                                    .tz(publisher.Timezone)
                                    .format('MMM DD, YYYY - hh:mm A')}]`}</Text>
                                </Col>
                              </Cols>
                            </Row>
                            <Row noPadding>
                              <Text
                                small
                                style={{
                                  backgroundColor: publicaColors.veryLightBlue,
                                  borderRadius: borderRadius.base,
                                  padding: '8px 16px',
                                }}
                              >
                                {history.Note}
                              </Text>
                            </Row>
                          </Rows>
                        </Row>
                      );
                    })}
                  </Rows>
                </Row>
              </Rows>
            </Row>
            {!OfferDeclinedCanceledExpired.includes(activeRowInfo?.CustomLeftColumn?.value) && (
              <RowLineBreak />
            )}
            {/* Make Offer Button */}
            {OfferDeclinedCanceledExpired.includes(activeRowInfo?.CustomLeftColumn?.value) ? (
              <></>
            ) : onlyCancelButton ? (
              <Row
                style={{
                  textAlign: 'center',
                }}
              >
                <ButtonV2
                  hoverColor='#ECECEC'
                  backgroundColor={publicaColors.lighterGray}
                  style={{
                    borderRadius: borderRadius.small,
                    padding: spacing.small,
                    minWidth: '80px',
                    minHeight: '32px',
                  }}
                  aria-label='Cancel Offer'
                  onClick={() => handleCancelPlatformConnection()}
                  disabled={
                    !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                  }
                >
                  <IconText
                    bold
                    extraSmall
                    gap={spacing.extraSmall}
                    icon='close'
                    text='Cancel Offer'
                    noPadding
                  />
                </ButtonV2>
              </Row>
            ) : (
              <Row paddingTop={spacing.none} paddingBottom={spacing.large}>
                <Rows
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Row noPadding>
                    <Cols
                      smallVerticalPadding
                      gap={spacing.large}
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {/* Accept Button */}
                      {!isMakingCounterOffer && (
                        <Col>
                          <ButtonV2
                            style={{
                              borderRadius: borderRadius.small,
                              padding: spacing.small,
                            }}
                            aria-label='Accept'
                            onClick={() => handleAcceptPlatformConnection()}
                            disabled={
                              !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                .isAuthorized
                            }
                          >
                            <IconText
                              bold
                              extraSmall
                              gap={spacing.extraSmall}
                              color={publicaColors.white}
                              icon='flame'
                              text='Accept'
                              noPadding
                            />
                          </ButtonV2>
                        </Col>
                      )}
                      {/* Counter Offer Button */}
                      <Col>
                        {!isMakingCounterOffer ? (
                          <ButtonV2
                            hoverColor='#ECECEC'
                            backgroundColor={publicaColors.lighterGray}
                            style={{
                              borderRadius: borderRadius.small,
                              padding: spacing.small,
                            }}
                            aria-label='Counter'
                            onClick={() => setIsMakingCounterOffer(true)}
                            disabled={
                              !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                .isAuthorized
                            }
                          >
                            {publisher.ID === activeRowInfo?.BuyerPublisherID?.value && (
                              <IconText
                                bold
                                extraSmall
                                gap={spacing.extraSmall}
                                color={publicaColors.blue}
                                icon='refresh'
                                text='Counter'
                                noPadding
                              />
                            )}
                            {publisher.ID === activeRowInfo?.SellerPublisherID?.value && (
                              <IconText
                                bold
                                extraSmall
                                gap={spacing.extraSmall}
                                color={publicaColors.blue}
                                icon='refresh'
                                text='Make Counter Offer'
                                noPadding
                              />
                            )}
                          </ButtonV2>
                        ) : (
                          <ButtonV2
                            style={{
                              borderRadius: borderRadius.small,
                              padding: spacing.small,
                            }}
                            aria-label='Counter'
                            onClick={() =>
                              !finalConfirmCounterOffer
                                ? setFinalConfirmCounterOffer(true)
                                : onClickCounterOffer()
                            }
                            disabled={
                              !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                .isAuthorized
                            }
                          >
                            <IconText
                              bold
                              extraSmall
                              gap={spacing.extraSmall}
                              color={publicaColors.white}
                              icon='refresh'
                              text={
                                !finalConfirmCounterOffer
                                  ? 'Send Counter Offer'
                                  : 'Confirm Counter Offer'
                              }
                              noPadding
                            />
                          </ButtonV2>
                        )}
                      </Col>
                      {/* Decline/Cancel Button */}
                      {
                        <Col>
                          {!isMakingCounterOffer ? (
                            <ButtonV2
                              hoverColor='#ECECEC'
                              backgroundColor={publicaColors.lighterGray}
                              style={{
                                borderRadius: borderRadius.small,
                                padding: spacing.small,
                              }}
                              aria-label='Decline'
                              onClick={() => handleDeclinePlatformConnection()}
                              disabled={
                                !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                  .isAuthorized
                              }
                            >
                              <IconText
                                bold
                                extraSmall
                                gap={spacing.extraSmall}
                                icon='close'
                                text='Decline'
                                noPadding
                              />
                            </ButtonV2>
                          ) : (
                            <ButtonV2
                              hoverColor='#ECECEC'
                              backgroundColor={publicaColors.lighterGray}
                              style={{
                                borderRadius: borderRadius.small,
                                padding: spacing.small,
                              }}
                              aria-label='Cancel'
                              onClick={onClickCancel}
                            >
                              <IconText
                                bold
                                extraSmall
                                gap={spacing.extraSmall}
                                icon='close'
                                text='Cancel'
                                noPadding
                              />
                            </ButtonV2>
                          )}
                        </Col>
                      }
                    </Cols>
                  </Row>
                  {activeRowInfo?.BuyerPublisherID?.value === publisher.ID &&
                    !finalConfirmCounterOffer && (
                      <Row noVerticalPadding>
                        <Div displayFlex>
                          <Text extraSmall color={publicaColors.darkGray}>
                            {`Clicking 'Accept' will allow you to adjust the bidding details and activate the connection.`}
                          </Text>
                        </Div>
                      </Row>
                    )}
                  {activeRowInfo?.SellerPublisherID?.value === publisher.ID &&
                    isMakingCounterOffer &&
                    !finalConfirmCounterOffer && (
                      <Row noPadding>
                        <Div displayFlex>
                          <Text extraSmall color={publicaColors.darkGray}>
                            {`Connection only starts when buyer accepts the counter offer.`}
                          </Text>
                        </Div>
                      </Row>
                    )}
                  {finalConfirmCounterOffer && (
                    <Row noPadding>
                      <Div displayFlex>
                        <Text extraSmall color={publicaColors.darkGray}>
                          <Span bold>Check check </Span>- Counter Offer CPM looks good?
                        </Text>
                      </Div>
                    </Row>
                  )}
                </Rows>
              </Row>
            )}
          </Rows>
        ) : (
          <Rows style={{ width: '40vw', overflow: 'hidden' }}>
            <Row verticalPadding paddingBottom={spacing.none}>
              <Cols
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Col>
                  <TextWithBackground color='purple' text='New Connection' />
                </Col>
                <Col>
                  <TextButton
                    color={publicaColors.darkGray}
                    style={{
                      borderRadius: borderRadius.small,
                      padding: spacing.small,
                    }}
                    aria-label='Cancel'
                    onClick={onCloseDrawer}
                  >
                    <IconText
                      bold
                      extraSmall
                      gap={spacing.extraSmall}
                      color={publicaColors.darkGray}
                      icon='close'
                      text='Close'
                      noPadding
                    />
                  </TextButton>
                </Col>
              </Cols>
            </Row>
            <Row noPadding>
              <Rows>
                <Row paddingBottom={spacing.small} paddingLeft={spacing.large}>
                  <Text large heavy>
                    Final step: Set the Connection Live
                  </Text>
                </Row>
                <Row noPadding>
                  <Rows
                    gap={spacing.small}
                    marginLeft={spacing.medium}
                    marginRight={spacing.medium}
                    style={{
                      backgroundColor: publicaColors.veryLightBlue,
                    }}
                  >
                    <Row smallPadding paddingBottom={spacing.none}>
                      <IconText
                        small
                        icon='swap'
                        text='New connection, how does it work?'
                        noPadding
                      />
                    </Row>
                    <Row smallPadding paddingTop={spacing.none}>
                      <Text extraSmall style={{ lineHeight: '16px' }}>
                        {`A new channel is auto-created to reflect the seller's inventory in your system. 
                            Below, you will select which bidders should target this newly created channel. 
                            Once the connection is set live, you will be able to see it under the "Connections" tab.`}
                      </Text>
                    </Row>
                  </Rows>
                </Row>
              </Rows>
            </Row>
            <Row noPadding>
              <Rows>
                <Row paddingBottom={spacing.none}>
                  <IconText small icon='bidders' text='Bidder' />
                </Row>
                <Row>
                  <Cols style={{ alignItems: 'start' }}>
                    <Col>
                      <Switch
                        checked={useBidAdjSwitch}
                        disabled={
                          !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setUseBidAdjSwitch(e.target.checked)
                        }
                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                          e.stopPropagation()
                        }
                        color='secondary'
                        size='small'
                      />
                    </Col>
                    <Col>
                      <Rows>
                        <Row noVerticalPadding smallHorizontalPadding>
                          Use Bid Adjustment
                        </Row>
                        <Row noPadding paddingLeft={spacing.small}>
                          <Cols gap={spacing.small}>
                            <Col width={48}>
                              <FocusedTextfield
                                enableNumbersOnly
                                enablePositiveValuesOnly
                                enableAlignRight
                                type='number'
                                placeholder={'Adj. %'}
                                helperText={'Adj. %'}
                                multiline
                                label=''
                                name={'Bid Adjustment Percentage'}
                                value={bidAdjPercentage}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setBidAdjPercentage(event.target.value)
                                }
                                disabled={!useBidAdjSwitch}
                              />
                            </Col>
                            <Col width={48}>
                              <FocusedTextfield
                                enableNumbersOnly
                                enablePositiveValuesOnly
                                enableAlignRight
                                type='number'
                                placeholder={'Adj. $'}
                                helperText={'Adj. $'}
                                multiline
                                label=''
                                name={'Bid Adjustment Value'}
                                value={bidAdjValue}
                                disabled={!useBidAdjSwitch}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setBidAdjValue(event.target.value)
                                }
                              />
                            </Col>
                          </Cols>
                        </Row>
                      </Rows>
                    </Col>
                    <Col>
                      <InfoTooltipIcon
                        tooltipText={`Use Bid Adjustment to reflect margin with seller, if any.`}
                        tooltipPlacement='top'
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row>
                  <Cols style={{ alignItems: 'start' }}>
                    <Col>
                      <Switch
                        checked={targetExistingBiddersSwitch}
                        disabled={
                          !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setTargetExistingBiddersSwitch(e.target.checked)
                        }
                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                          e.stopPropagation()
                        }
                        color='secondary'
                        size='small'
                      />
                    </Col>
                    <Col paddingTop='2px'>
                      <Rows>
                        <Row noVerticalPadding smallHorizontalPadding>
                          Target existing bidders
                        </Row>
                        <Row smallPadding>
                          <SelectPill
                            customLabel={`${
                              Object.entries(selectedBidders).length || 0
                            } bidders selected`}
                            currentSelected={selectedBidders}
                            displayID={true}
                            filterKey={'Bidders'}
                            handleAccept={handleAccept}
                            isActive={false}
                            isSubset={true}
                            isDisabled={!targetExistingBiddersSwitch}
                            options={cloneDeep(currBiddersList)}
                            isRefreshing={loadState === LoadingStateLoading}
                          />
                        </Row>
                      </Rows>
                    </Col>
                    <Col>
                      <InfoTooltipIcon
                        tooltipText={`Select which bidders should target the channel representing the seller's inventory.`}
                        tooltipPlacement='top'
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row paddingBottom={spacing.none}>
                  <IconText small gap={spacing.small} icon='settings' text='Options' />
                </Row>
                <Row>
                  <Cols style={{ alignItems: 'start' }}>
                    <Col>
                      <Switch
                        checked={sendTxtToSeller}
                        disabled={
                          !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSendTxtToSeller(e.target.checked)
                        }
                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                          e.stopPropagation()
                        }
                        color='secondary'
                        size='small'
                      />
                    </Col>
                    <Col>
                      <Rows>
                        <Row noVerticalPadding smallHorizontalPadding>
                          Send ads.txt or app-ads.txt to seller
                        </Row>
                        <Row noPadding paddingLeft={spacing.small}>
                          <FocusedTextfield
                            type='number'
                            placeholder={'ads.txt sample'}
                            helperText={'ads.txt sample'}
                            multiline
                            fullWidth
                            label=''
                            name={'ads txt'}
                            value={txtToSeller}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              setTxtToSeller(event.target.value)
                            }
                            disabled={!sendTxtToSeller}
                            style={{
                              overflow: 'auto',
                              maxHeight: '20vh',
                            }}
                          />
                        </Row>
                      </Rows>
                    </Col>
                    <Col>
                      <InfoTooltipIcon
                        tooltipText={`Strongly advised to ensure smooth delivery`}
                        tooltipPlacement='top'
                      />
                    </Col>
                  </Cols>
                </Row>
              </Rows>
            </Row>
            <RowLineBreak />
            <Row verticalPadding>
              <Cols
                gap={spacing.large}
                style={{
                  justifyContent: 'center',
                }}
              >
                <Col>
                  <ButtonV2
                    style={{
                      borderRadius: borderRadius.small,
                      padding: spacing.small,
                    }}
                    aria-label='Confirm & Set Live'
                    disabled={Object.entries(selectedBidders).length === 0 || isActionPending}
                    onClick={debounce(handleBuyerFinalConfirmation, 400)}
                  >
                    <IconText
                      bold
                      extraSmall
                      gap={spacing.extraSmall}
                      color={publicaColors.white}
                      icon='checkmark-plain'
                      text='Confirm & Set Live'
                      noPadding
                    />
                  </ButtonV2>
                </Col>
                <Col>
                  <ButtonV2
                    hoverColor='#ECECEC'
                    backgroundColor={publicaColors.lighterGray}
                    style={{
                      borderRadius: borderRadius.small,
                      padding: spacing.small,
                    }}
                    aria-label='Cancel'
                    onClick={() => {
                      setIsBuyerFinalAccept(false);
                    }}
                  >
                    <IconText
                      bold
                      extraSmall
                      gap={spacing.extraSmall}
                      noPadding
                      icon='close'
                      text='Cancel'
                    />
                  </ButtonV2>
                </Col>
              </Cols>
            </Row>
          </Rows>
        )}
      </Drawer>
    </>
  );
};
